import { apiEndpoints, config } from '../config/env'
import { apiService } from './apiService'

export interface saveJSASignatureResponse {
  FileName: string
}

export interface JSAData {
  JSAID?: number
  techID_FK: number
  jsaDate: string
  confirmation: boolean
  action: boolean
  jobStep1?: string
  hazard1?: string
  action1?: string
  who1?: string
  jobStep2?: string
  hazard2?: string
  action2?: string
  who2?: string
  jobStep3?: string
  hazard3?: string
  action3?: string
  who3?: string
  jsaSignature?: string
  technicianName?: string
}

export interface JSASignatureData {
  signatureBase64: string
  techID_FK: number
  jsaDate: string
}

export class JSAService {
  private static instance: JSAService
  
  public static getInstance(): JSAService {
    if (!JSAService.instance) {
      JSAService.instance = new JSAService()
    }
    return JSAService.instance
  }

  async getJSAByTechAndDate(techId: number, jsaDate?: string): Promise<JSAData | null> {
    try {
      const dateParam = jsaDate ? `?jsaDate=${jsaDate}` : ''
      const url = `${apiEndpoints.jsa}/technician/${techId}${dateParam}`
      return await apiService.get<JSAData>(url)
    } catch (error) {
      // Return null if JSA doesn't exist (404) or other errors in development
      if (config.isDevelopment) {
        return null
      }
      throw error
    }
  }

  async getAllJSAsForTech(techId: number): Promise<JSAData[]> {
    try {
      const url = `${apiEndpoints.jsa}/technician/${techId}/all`
      return await apiService.get<JSAData[]>(url)
    } catch (error) {
      if (config.isDevelopment) {
        return []
      }
      throw error
    }
  }

  async checkJSAExists(techId: number, jsaDate: string): Promise<boolean> {
    try {
      const url = `${apiEndpoints.jsa}/exists?techId=${techId}&jsaDate=${jsaDate}`
      const response = await apiService.get<{ exists: boolean }>(url)
      return response.exists
    } catch (error) {
      if (config.isDevelopment) {
        return false
      }
      throw error
    }
  }

  async createJSA(jsaData: JSAData): Promise<JSAData> {
    try {
      return await apiService.post<JSAData>(apiEndpoints.jsa, jsaData)
    } catch (error) {
      throw error
    }
  }

  async saveJSASignature(signatureData: JSASignatureData): Promise<{ success: boolean; message: string, fileName: string }> {
    try {
      const url = `${apiEndpoints.jsa}/signature`
      return await apiService.post<{ success: boolean; message: string, fileName: string }>(url, signatureData)
    } catch (error) {
      throw error
    }
  }

  // Local storage methods for signature persistence
  saveSignatureToStorage(signatureBase64: string): void {
    try {
      localStorage.setItem('coastsmoke_jsa_signature', signatureBase64)
    } catch (error) {
    }
  }

  getSignatureFromStorage(): string | null {
    try {
      return localStorage.getItem('coastsmoke_jsa_signature')
    } catch (error) {
      return null
    }
  }

  clearSignatureFromStorage(): void {
    try {
      localStorage.removeItem('coastsmoke_jsa_signature')
    } catch (error) {
    }
  }

  // Development fallback method
  developmentFallback(techId: number, completedBy: string): JSAData {
    const today = new Date().toISOString().split('T')[0]
    return {
      techID_FK: techId,
      jsaDate: today,
      confirmation: false,
      action: false,
      technicianName: completedBy,
      jobStep1: '',
      hazard1: '',
      action1: '',
      who1: '',
      jobStep2: '',
      hazard2: '',
      action2: '',
      who2: '',
      jobStep3: '',
      hazard3: '',
      action3: '',
      who3: ''
    }
  }
}

export const jsaService = JSAService.getInstance() 